/**
 * Survey Responses API
 *
 * Handles survey response submission and analytics.
 * Includes helpers for capturing geolocation and client info.
 */

import { api } from './client'

/**
 * Submit a survey response
 *
 * @param {string} surveyId - Survey UUID
 * @param {Object} params
 * @param {Object} params.answers - Question answers { questionName: value }
 * @param {number} [params.durationMs] - Time spent in milliseconds
 * @param {Object} [params.geolocation] - Geolocation data
 * @param {string} [params.timezone] - Browser timezone
 * @param {string} [params.language] - Browser language
 * @param {'browser'|'url_param'|'user_choice'} [params.languageSource] - How language was determined
 * @param {Object} [params.clientInfo] - Browser/device info
 * @param {Object} [params.meta] - Additional metadata
 * @param {string} [params.fieldAgentToken] - Field agent token for field responses
 * @returns {Promise<Object>} Created response
 */
export async function submitResponse(
  surveyId,
  {
    answers,
    durationMs = null,
    geolocation = null,
    timezone = null,
    language = null,
    languageSource = null,
    clientInfo = null,
    meta = null,
    fieldAgentToken = null,
  }
) {
  return api.post(
    `/surveys/${surveyId}/responses`,
    {
      answers,
      duration_ms: durationMs,
      geolocation,
      timezone,
      language,
      language_source: languageSource,
      client_info: clientInfo,
      meta,
      field_agent_token: fieldAgentToken,
    },
    { auth: false }
  ) // Responses can be submitted without auth
}

/**
 * Start a response session (for multi-page surveys)
 *
 * @param {string} surveyId - Survey UUID
 * @param {Object} [clientInfo] - Optional client info
 * @returns {Promise<Object>} Response with ID for subsequent saves
 */
export async function startResponse(surveyId, clientInfo = null) {
  return api.post(
    `/surveys/${surveyId}/responses/start`,
    {
      client_info: clientInfo,
    },
    { auth: false }
  )
}

/**
 * List responses for a survey (owner only)
 *
 * @param {string} surveyId - Survey UUID
 * @param {Object} [params]
 * @param {boolean} [params.completeOnly=true] - Only completed responses
 * @param {number} [params.limit=100] - Max results
 * @param {number} [params.offset=0] - Pagination offset
 * @returns {Promise<Object>} Paginated list of responses
 */
export async function listResponses(
  surveyId,
  { completeOnly = true, limit = 100, offset = 0 } = {}
) {
  return api.get(`/surveys/${surveyId}/responses`, {
    complete_only: completeOnly,
    limit,
    offset,
  })
}

/**
 * Fetch ALL responses for a survey by paginating through all pages.
 * Used for full data exports (Excel).
 *
 * @param {string} surveyId - Survey UUID
 * @param {Object} [params]
 * @param {boolean} [params.completeOnly=true] - Only completed responses
 * @param {function} [params.onProgress] - Progress callback ({ fetched, total })
 * @returns {Promise<Array>} All response items
 */
export async function fetchAllResponses(surveyId, { completeOnly = true, onProgress } = {}) {
  const pageSize = 500
  let offset = 0
  let allItems = []

  while (true) {
    const result = await listResponses(surveyId, { completeOnly, limit: pageSize, offset })
    allItems = allItems.concat(result.items || [])

    if (onProgress) {
      onProgress({ fetched: allItems.length, total: result.total || allItems.length })
    }

    if (!result.has_more) break
    offset += pageSize
  }

  return allItems
}

/**
 * Get a specific response (owner only)
 *
 * @param {string} surveyId - Survey UUID
 * @param {string} responseId - Response UUID
 * @returns {Promise<Object>} Response details with answers
 */
export async function getResponse(surveyId, responseId) {
  return api.get(`/surveys/${surveyId}/responses/${responseId}`)
}

/**
 * Get analytics for a survey (owner only)
 *
 * @param {string} surveyId - Survey UUID
 * @returns {Promise<Object>} Analytics summary
 */
export async function getAnalytics(surveyId) {
  return api.get(`/surveys/${surveyId}/analytics`)
}

// ==========================================
// Helper Functions for Data Capture
// ==========================================

/**
 * Capture precise location through the browser Geolocation API.
 * Callers must obtain explicit user consent before invoking this helper.
 * IP-based lookup is deliberately excluded: it discloses the respondent's IP
 * to a third party and cannot be treated as a consent-free fallback.
 *
 * @param {Object} [options]
 * @param {number} [options.gpsTimeout=8000] - GPS timeout in ms
 * @returns {Promise<Object|null>} Geolocation data or null on error
 */
export async function captureGeolocation({ gpsTimeout = 8000 } = {}) {
  return _captureGPS(gpsTimeout)
}

/**
 * Try to get precise GPS coordinates from the browser.
 * Returns null if geolocation is denied, unavailable, or times out.
 *
 * @param {number} timeout - Timeout in ms
 * @returns {Promise<Object|null>}
 */
async function _captureGPS(timeout) {
  if (!navigator.geolocation) {
    return null
  }

  try {
    const position = await new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout,
        maximumAge: 60000, // Accept cached position up to 1 minute old
      })
    })

    return {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
      accuracy: position.coords.accuracy, // in meters
      location_source: 'gps',
      // city/region/country will be populated by backend via GeoNames
    }
  } catch {
    return null
  }
}

/**
 * Capture client/browser information
 *
 * @returns {Object} Client info object
 */
export function captureClientInfo() {
  const ua = navigator.userAgent

  // Detect browser
  let browser = 'Unknown'
  let browserVersion = ''

  if (ua.includes('Firefox/')) {
    browser = 'Firefox'
    browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Edg/')) {
    browser = 'Edge'
    browserVersion = ua.match(/Edg\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Chrome/')) {
    browser = 'Chrome'
    browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    browser = 'Safari'
    browserVersion = ua.match(/Version\/(\d+)/)?.[1] || ''
  } else if (ua.includes('Opera') || ua.includes('OPR/')) {
    browser = 'Opera'
    browserVersion = ua.match(/(?:Opera|OPR)\/(\d+)/)?.[1] || ''
  }

  // Detect OS
  let os = 'Unknown'
  let osVersion = ''

  if (ua.includes('Windows')) {
    os = 'Windows'
    if (ua.includes('Windows NT 10')) osVersion = '10'
    else if (ua.includes('Windows NT 11')) osVersion = '11'
  } else if (ua.includes('Mac OS X')) {
    os = 'macOS'
    osVersion = ua.match(/Mac OS X (\d+[._]\d+)/)?.[1]?.replace('_', '.') || ''
  } else if (ua.includes('Linux')) {
    os = 'Linux'
  } else if (ua.includes('Android')) {
    os = 'Android'
    osVersion = ua.match(/Android (\d+)/)?.[1] || ''
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    os = 'iOS'
    osVersion = ua.match(/OS (\d+)/)?.[1] || ''
  }

  // Detect device type
  let device = 'desktop'
  if (/Mobile|Android|iPhone|iPod/.test(ua)) {
    device = 'mobile'
  } else if (/iPad|Tablet/.test(ua)) {
    device = 'tablet'
  }

  return {
    browser,
    browser_version: browserVersion,
    os,
    os_version: osVersion,
    device,
    screen_width: window.screen?.width || null,
    screen_height: window.screen?.height || null,
    user_agent: ua,
  }
}

/**
 * Get browser timezone
 *
 * @returns {string} Timezone string (e.g., 'America/New_York')
 */
export function getTimezone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone
  } catch {
    return 'UTC'
  }
}

/**
 * Get browser language
 *
 * @returns {string} Language code (e.g., 'en-US')
 */
export function getLanguage() {
  return navigator.language || navigator.userLanguage || 'en'
}

/**
 * Capture all response metadata at once
 *
 * @param {Object} [options]
 * @param {boolean} [options.includeGeolocation=false] - Request geolocation only after consent
 * @returns {Promise<Object>} All metadata
 */
export async function captureResponseMetadata({ includeGeolocation = false } = {}) {
  const geolocation = includeGeolocation ? await captureGeolocation() : null

  return {
    geolocation,
    timezone: getTimezone(),
    language: getLanguage(),
    clientInfo: captureClientInfo(),
  }
}

/**
 * Determine language source based on how language was selected
 *
 * @param {Object} options
 * @param {string} [options.urlLang] - Language from URL parameter (e.g., ?lang=hi)
 * @param {string} [options.userSelectedLang] - Language manually selected by user
 * @returns {{ language: string, languageSource: 'browser'|'url_param'|'user_choice' }}
 */
export function determineLanguageSource({ urlLang = null, userSelectedLang = null } = {}) {
  // Priority: user choice > URL param > browser default
  if (userSelectedLang) {
    return {
      language: userSelectedLang,
      languageSource: 'user_choice',
    }
  }

  if (urlLang) {
    return {
      language: urlLang,
      languageSource: 'url_param',
    }
  }

  return {
    language: getLanguage(),
    languageSource: 'browser',
  }
}

/**
 * Get language from URL search params
 *
 * @returns {string|null} Language code from URL or null
 */
export function getLanguageFromUrl() {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  return params.get('lang') || params.get('language') || null
}
